import { Empty } from "@bednbody/ui/components/empty";
import { Button } from "@bednbody/ui/components/button";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { BuildingsIcon, CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { ReadFailedCard } from "@bednbody/ui/components/read-failed-card";
import { useMemo, useState } from "react";
import { cn } from "@bednbody/ui/lib/utils";
import { minutesSince } from "@/lib/time";
import type { CommandBooking } from "../command-center.hooks";
import { PRE_SESSION_STATUSES } from "../command-center.hooks";
import type { useAttendance, useHubsList } from "../ops-dashboard.hooks";

type Hubs = NonNullable<ReturnType<typeof useHubsList>["data"]>;
type AttendanceRows = NonNullable<ReturnType<typeof useAttendance>["data"]>;

interface HubPerf {
  id: string;
  name: string;
  region: string | null;
  isPilot: boolean;
  bookingsToday: number;
  active: number;
  staffOnDuty: number;
  bookedValueToday: number;
  overdue: number;
}

const PAGE_SIZE = 6;

/**
 * Per-hub scoreboard for today, computed from the live command feed.
 * Customer rating is NOT shown — there is no ratings table in the schema
 * (Open Item), and we don't fabricate a score.
 */
export function HubPerformanceGrid({
  hubs,
  bookings,
  attendance,
  isLoading,
  failed = false,
}: {
  hubs: Hubs;
  bookings: CommandBooking[];
  attendance: AttendanceRows;
  isLoading: boolean;
  /** A failed read is not an unconfigured platform. */
  failed?: boolean;
}) {
  const [page, setPage] = useState(0);
  const perf = useMemo<HubPerf[]>(() => {
    const today = new Date().toDateString();
    const onDutyByHub = new Map<string, number>();
    for (const a of attendance) {
      if (!a.attendance?.onDuty || !a.hubName) continue;
      onDutyByHub.set(a.hubName, (onDutyByHub.get(a.hubName) ?? 0) + 1);
    }

    return hubs
      .map((h) => {
        const hubBookings = bookings.filter((b) => b.hub_id === h.id);
        const todays = hubBookings.filter((b) => new Date(b.scheduled_at).toDateString() === today);
        return {
          id: h.id,
          name: h.name,
          region: h.region,
          isPilot: h.is_pilot ?? false,
          bookingsToday: todays.length,
          active: hubBookings.filter((b) =>
            ["dispatched", "en_route_to_client", "arrived", "in_session"].includes(b.status),
          ).length,
          staffOnDuty: onDutyByHub.get(h.name) ?? 0,
          bookedValueToday: todays
            .filter((b) => b.status !== "cancelled")
            .reduce((sum, b) => sum + b.gross_amount, 0),
          overdue: hubBookings.filter(
            (b) =>
              (PRE_SESSION_STATUSES as readonly string[]).includes(b.status) &&
              minutesSince(b.scheduled_at) > 0,
          ).length,
        };
      })
      .sort(
        (a, b) =>
          b.overdue - a.overdue || b.active - a.active || b.bookedValueToday - a.bookedValueToday,
      );
  }, [hubs, bookings, attendance]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 motion-reduce:animate-none" />
        ))}
      </div>
    );
  }

  if (failed) {
    return (
      <ReadFailedCard message="Hub performance could not be read — the hubs exist, the numbers did not arrive." />
    );
  }

  if (perf.length === 0) {
    return <Empty>No hubs configured yet.</Empty>;
  }

  const pageCount = Math.max(1, Math.ceil(perf.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageRows = perf.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <div className="hidden grid-cols-[minmax(12rem,1.5fr)_repeat(5,minmax(4.5rem,0.8fr))] gap-4 border-b border-border bg-muted/40 px-4 py-2.5 type-body-micro font-medium uppercase tracking-wide text-muted-foreground md:grid">
        <span>Hub</span>
        <span>Scheduled</span>
        <span>Live</span>
        <span>On duty</span>
        <span>Health</span>
        <span className="text-right">Booked value</span>
      </div>
      <div className="divide-y divide-border">
        {pageRows.map((h) => {
          const health =
            h.overdue > 0
              ? { label: h.overdue + " overdue", className: "bg-destructive/10 text-destructive" }
              : h.bookingsToday === 0
                ? { label: "No work today", className: "bg-muted text-muted-foreground" }
                : h.active > 0
                  ? { label: "Live", className: "bg-success/15 text-success" }
                  : {
                      label: "Scheduled",
                      className: "bg-primary/10 text-primary-dark dark:text-primary-light",
                    };

          return (
            <div
              key={h.id}
              className="grid gap-3 px-4 py-3.5 transition-colors hover:bg-card-hover md:grid-cols-[minmax(12rem,1.5fr)_repeat(5,minmax(4.5rem,0.8fr))] md:items-center md:gap-4"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary-dark dark:text-primary-light">
                  <BuildingsIcon size={15} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{h.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {h.region ?? "No region"}
                    {h.isPilot ? " · Pilot" : ""}
                  </p>
                </div>
              </div>

              <div className="flex items-baseline justify-between md:block">
                <span className="text-xs text-muted-foreground md:hidden">Scheduled</span>
                <span className="text-sm font-medium tabular-nums text-foreground">
                  {h.bookingsToday}
                </span>
              </div>
              <div className="flex items-baseline justify-between md:block">
                <span className="text-xs text-muted-foreground md:hidden">Live</span>
                <span className="text-sm font-medium tabular-nums text-foreground">{h.active}</span>
              </div>
              <div className="flex items-baseline justify-between md:block">
                <span className="text-xs text-muted-foreground md:hidden">On duty</span>
                <span className="text-sm font-medium tabular-nums text-foreground">
                  {h.staffOnDuty}
                </span>
              </div>
              <div className="flex items-center justify-between md:block">
                <span className="text-xs text-muted-foreground md:hidden">Health</span>
                <span
                  className={cn(
                    "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                    health.className,
                  )}
                >
                  {health.label}
                </span>
              </div>
              <div className="flex items-baseline justify-between md:text-right">
                <span className="text-xs text-muted-foreground md:hidden">Booked value</span>
                <span className="text-sm font-semibold tabular-nums text-foreground">
                  ₱{h.bookedValueToday.toLocaleString("en-PH")}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between border-t border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
        <span>
          {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, perf.length)} of {perf.length} hubs
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            type="button"
            onClick={() => setPage((current) => Math.max(0, current - 1))}
            disabled={safePage === 0}
            aria-label="Previous hub page"
          >
            <CaretLeftIcon />
          </Button>
          <span className="min-w-12 text-center tabular-nums">
            {safePage + 1} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="icon-sm"
            type="button"
            onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}
            disabled={safePage >= pageCount - 1}
            aria-label="Next hub page"
          >
            <CaretRightIcon />
          </Button>
        </div>
      </div>
    </div>
  );
}
